import { memo } from 'react';

/**
 * Premium per-service color tokens used to tint a sidebar icon when its
 * service has a job actively running. Keys match `entitlements.js`.
 *
 * The actual color tokens live in `styles/index.css` under
 * `.service-icon[data-service="…"] { --service-color: … }`. This object is
 * the single source of truth for which services are "themable". CSS reads
 * the value via the `data-service` attribute.
 */
const KNOWN_SERVICES = new Set([
  'ab_testing',
  'heatmaps',
  'clickpath',
  'campaigns',
  'seomax',
  'cwv',
  'swiss',
]);

/**
 * Wrapper around a sidebar icon that adds (only when `running` is true):
 *   - a soft heartbeat throb on the icon itself
 *   - a "lifeline" ping ring expanding around the icon
 *   - a service-specific accent color
 *
 * When idle, the wrapper is visually identical to a bare `<Icon />` — same
 * stroke color (inherited from the link), same size, no animation, no halo.
 *
 * Respects `prefers-reduced-motion` via CSS in `styles/index.css`.
 *
 * @param {{
 *   icon: import('react').ComponentType<{ size?: number, className?: string, 'aria-hidden'?: boolean }>,
 *   service?: string,
 *   size?: number,
 *   running?: boolean,
 *   className?: string,
 * }} props
 */
function ServiceIcon({ icon: Icon, service, size = 20, running = false, className = '' }) {
  const dataService = service && KNOWN_SERVICES.has(service) ? service : undefined;
  const isRunning = !!running && !!dataService;
  const wrapperClass = [
    'service-icon',
    isRunning ? 'service-icon-running' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={wrapperClass}
      data-service={dataService}
      data-running={isRunning ? 'true' : undefined}
      aria-hidden="true"
    >
      <Icon size={size} className="service-icon-svg shrink-0" aria-hidden />
    </span>
  );
}

export default memo(ServiceIcon);
